import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { investmentsService, bankTransactionsService, bankAccountsService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, mensagemDoErroDeCarga, textos } from '../design';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { lerVarredura, temDriftInexplicado, fraseDoSaldoDaVarredura } from '../utils/contaDeVarredura';

const fmt = (v: any) => {
  const n = parseFloat(v);
  if (!isFinite(n)) return '0,00';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
/** Cotas têm 8 casas no Sicredi. Cortar antes disso perde centavos no principal. */
const fmtQ = (v: any) => {
  const n = parseFloat(v);
  if (!isFinite(n)) return '0';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
};

/** Hoje em BRT. `toISOString()` devolveria ontem entre 00h e 03h. */
const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const daysBetween = (from: string, to: string) => {
  const a = Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10));
  const b = Date.UTC(+to.slice(0, 4), +to.slice(5, 7) - 1, +to.slice(8, 10));
  return Math.round((b - a) / 86400000);
};

/** Depois de quantos dias a posição informada pelo banco deixa de ser confiável. */
const POSITION_STALE_DAYS = 7;

type Tab = 'aplicacao' | 'resgate' | 'posicao';

interface Props {
  account: any;
  onChanged: () => void;
  onBack: () => void;
}

export default function InvestmentAccountPanel({ account, onChanged, onBack }: Props) {
  const [summary, setSummary] = useState<any>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [redemptions, setRedemptions] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [modal, setModal] = useState<Tab | null>(null);
  const [importing, setImporting] = useState(false);
  const [expandedRedemption, setExpandedRedemption] = useState<number | null>(null);

  // ⛔ A CARGA NÃO CALA (10/10/2026, medição do item 13). O catch só logava: o saldo caía no `currentBalance` da conta
  // em verde, sumiam a data da posição e o alerta de desatualizada, as três tabelas diziam "Nenhuma
  // aplicação/resgate/posição registrada", e "+ Registrar movimento" seguia ativo. O servidor não deduplica lote:
  // o operador relançava o lote que existe, e lote duplicado quebra o FIFO que sustenta a conta de IOF.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const load = useCallback(async () => {
    await rodarCarga(async () => {
      const [s, a, r, p] = await Promise.all([
        investmentsService.getSummary(account.id),
        investmentsService.getApplications(account.id),
        investmentsService.getRedemptions(account.id),
        investmentsService.getPositions(account.id),
      ]);
      setSummary(s);
      setApplications(Array.isArray(a) ? a : []);
      setRedemptions(Array.isArray(r) ? r : []);
      setPositions(Array.isArray(p) ? p : []);
    });
  }, [account.id, rodarCarga]);
  const loading = carga.estado === 'carregando';
  /** Leu alguma vez? Sem leitura, nenhum número do painel tem fonte. */
  const temFonte = carga.atualizadoEm !== null;
  const semFonte = textos.numeroSemFonte;
  /** Registrar sem as listas lidas é registrar sem ver se o lote já existe (o servidor não deduplica). */
  const porqueNaoRegistrar = carga.estado === 'pronto' ? null
    : carga.estado === 'carregando' ? 'Carregando os lotes…'
    : 'Sem as listas lidas agora, registrar pode duplicar um lote que já existe.';

  useEffect(() => { load(); }, [load]);

  const afterWrite = async () => {
    setModal(null);
    await load();
    onChanged();
  };

  const positionAge = useMemo(() => {
    if (!summary?.lastPosition?.date) return null;
    return daysBetween(summary.lastPosition.date, todayStr());
  }, [summary]);

  const stale = positionAge != null && positionAge > POSITION_STALE_DAYS;

  // Conta de VARREDURA com o resumo da #440 (ver utils/contaDeVarredura). null = o painel de sempre (fundo com lotes, ou
  // backend sem a #440).
  const varredura = useMemo(() => lerVarredura(summary), [summary]);
  // Derivado não é mais "incompleto" (backend #448): é o saldo, calculado da corrente a cada import. Alerta fica para a
  // posição velha e para quando não há saldo nenhum.
  const saldoDaVarreduraIncompleto = varredura != null
    && (varredura.origem === 'indisponivel' || (varredura.origem === 'posicao' && (varredura.idadeDaPosicaoEmDias ?? 0) > POSITION_STALE_DAYS));

  // Conta de VARREDURA (14/10/2026): no lugar dos lotes, resgates e posições do modelo Sicredi (que nela são sempre vazios
  // e falam de cota, IOF e IRRF), as movimentações do fundo na conta CORRENTE, as mesmas linhas que o fundSweep soma.
  const cargaDasMovimentacoes = useEstadoDaCarga();
  const rodarMovimentacoes = cargaDasMovimentacoes.rodar;
  const [movimentacoes, setMovimentacoes] = useState<any[]>([]);
  const ehVarredura = varredura != null;
  const carregarMovimentacoes = useCallback(async () => {
    if (!account.parentAccountId) return;
    await rodarMovimentacoes(async () => {
      const previa = await bankAccountsService.previaVarreduras(account.parentAccountId);
      const linhas = Array.isArray(previa?.linhas) ? previa.linhas : [];
      // mais recente primeiro, como as outras listas do módulo
      setMovimentacoes([...linhas].sort((a, b) => String(b.date).localeCompare(String(a.date)) || b.id - a.id));
    });
  }, [account.parentAccountId, rodarMovimentacoes]);
  useEffect(() => { if (ehVarredura) carregarMovimentacoes(); }, [ehVarredura, carregarMovimentacoes]);
  // Enquanto o resumo não chegou não se sabe se é varredura: sem os botões, em vez de mostrar e tirar.
  const mostrarAcoesDoSicredi = !ehVarredura && (temFonte || carga.estado === 'erro');

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="text-slate-400 hover:text-white text-sm flex items-center gap-1.5">
          ← Voltar para contas
        </button>
        {mostrarAcoesDoSicredi && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setImporting(true)}
            className="px-4 py-2 bg-slate-700 text-slate-100 rounded-lg hover:bg-slate-600 text-sm font-medium"
          >
            Importar extrato (PDF)
          </button>
          <button
            onClick={() => setModal('aplicacao')}
            disabled={!!porqueNaoRegistrar}
            title={porqueNaoRegistrar || undefined}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Registrar movimento
          </button>
        </div>
        )}
      </div>

      {porqueNaoRegistrar && carga.estado === 'erro' && (
        <p className={`text-xs ${TONE_TEXT.warning}`} data-testid="porque-nao-registrar">{porqueNaoRegistrar}</p>
      )}

      {/* ===== Resumo ===== */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-slate-400 text-sm">{account.investmentProduct || 'Aplicação financeira'}</p>
            {varredura ? (
              <>
                <p className={`text-3xl font-bold mt-1 ${temFonte && varredura.saldo != null ? 'text-green-400' : 'text-slate-500'}`} data-kpi="saldo-aplicado">
                  {!temFonte ? semFonte : varredura.saldo != null ? `R$ ${fmt(varredura.saldo)}` : '—'}
                </p>
                <p className={`text-xs mt-1 ${saldoDaVarreduraIncompleto ? TONE_TEXT.warning : 'text-slate-500'}`} data-saldo-da-varredura={varredura.origem}>
                  {fraseDoSaldoDaVarredura(varredura, fmtDate)}
                </p>
                {/* O aviso do servidor vai junto do saldo. No derivado a frase acima já diz o mesmo: não repete. */}
                {varredura.aviso && varredura.origem !== 'derivado' && (
                  <p className={`text-xs mt-0.5 ${TONE_TEXT.warning}`} data-aviso-da-posicao>{varredura.aviso}</p>
                )}
              </>
            ) : (
            <>
            <p className={`text-3xl font-bold mt-1 ${temFonte ? 'text-green-400' : 'text-slate-500'}`} data-kpi="saldo-aplicado">{temFonte ? `R$ ${fmt(summary?.totalGross ?? account.currentBalance)}` : semFonte}</p>
            {summary?.lastPosition && (
              <p className={`text-xs mt-1 ${stale ? 'text-amber-400' : 'text-slate-500'}`}>
                Posição de {fmtDate(summary.lastPosition.date)}
                {positionAge === 0 ? ' (hoje)' : positionAge === 1 ? ' (ontem)' : ` (há ${positionAge} dias)`}
                {stale && ' — desatualizada, lance a posição nova'}
              </p>
            )}
            {!summary?.lastPosition && temFonte && (
              <p className="text-xs mt-1 text-amber-400">Nenhuma posição registrada — o saldo está pelo custo de aquisição</p>
            )}
            </>
            )}
          </div>

          {/* O quadro de liquidez e IOF é do fundo com lotes (Sicredi). Na varredura (14/10/2026) ele mostrava "—" e
              "Livre de IOF hoje R$ 0,00" em âmbar: alarme de um modelo que não se aplica à conta. */}
          {!varredura && (
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <div>
              <p className="text-slate-400 text-xs">Líquido para saque</p>
              <p className="text-lg font-semibold text-slate-200">
                {temFonte && summary?.lastPosition ? `R$ ${fmt(summary.lastPosition.netWithdrawable)}` : semFonte}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Rendimento acumulado</p>
              <p className={`text-lg font-semibold ${temFonte && summary?.accruedYield != null ? 'text-green-400' : 'text-slate-500'}`}>
                {temFonte && summary?.accruedYield != null ? `R$ ${fmt(summary.accruedYield)}` : semFonte}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Livre de IOF hoje</p>
              <p className={`text-lg font-semibold ${!temFonte ? 'text-slate-500' : (summary?.iofFreeGross || 0) > 0 ? 'text-green-400' : 'text-amber-400'}`}>
                {temFonte ? `R$ ${fmt(summary?.iofFreeGross || 0)}` : semFonte}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Preso por IOF</p>
              <p className="text-lg font-semibold text-slate-300">{temFonte ? `R$ ${fmt(summary?.iofLockedGross || 0)}` : semFonte}</p>
              {summary?.nextIofFreeDate && (summary?.iofLockedGross || 0) > 0 && (
                <p className="text-[11px] text-amber-400/80 mt-0.5">libera em {fmtDate(summary.nextIofFreeDate)}</p>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Varredura: o alerta é o drift INEXPLICADO (qualquer valor ≠ 0). O `drift` antigo (lotes − posição) não vale aqui:
            sem lote, ele acusava o saldo inteiro como aplicação não registrada. */}
        {varredura && temDriftInexplicado(varredura) && (
          <div className={`mt-4 rounded-lg p-3 text-sm border ${TONE_SURFACE.danger} ${TONE_BORDER.danger}`} role="alert" data-drift-inexplicado>
            <p className={`font-semibold ${TONE_TEXT.danger}`}>
              R$ {fmt(Math.abs(varredura.driftInexplicado as number))} que nem a posição do banco nem as linhas da conta corrente explicam
            </p>
            <p className="text-slate-300 mt-0.5">
              Descontado o rendimento retido que a posição declara, o que sobra é sinal: confira a posição do banco e as linhas de aplicação e resgate na conta corrente.
            </p>
          </div>
        )}
        {!varredura && summary?.drift != null && Math.abs(summary.drift) > 0.01 && (
          <div className="mt-4 bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-sm">
            <p className="text-red-300 font-semibold">
              O HUB calcula R$ {fmt(Math.abs(summary.drift))} {summary.drift > 0 ? 'a mais' : 'a menos'} que o relatório do banco
            </p>
            <p className="text-slate-300 mt-0.5">
              Quase sempre é aplicação ou resgate que aconteceu no banco e não foi registrado aqui.
              Confira a Movimentação do extrato de aplicação contra a lista de lotes abaixo.
            </p>
          </div>
        )}
      </div>

      {ehVarredura ? (
        <MovimentacoesDaVarredura
          temCorrente={!!account.parentAccountId}
          carga={cargaDasMovimentacoes}
          linhas={movimentacoes}
          onTentarDeNovo={carregarMovimentacoes}
        />
      ) : (
      <EstadoDaLista carga={carga} temDados={temFonte} oQue="os lotes, resgates e posições desta aplicação" onTentarDeNovo={load} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      <div className="space-y-4">
      {/* ===== Lotes (aplicações) ===== */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-white font-semibold">Aplicações (lotes)</h3>
          <span className="text-xs text-slate-500">O resgate consome do mais antigo para o mais novo</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-slate-400 text-xs">
              <tr>
                <th className="text-left px-5 py-2">Data</th>
                <th className="text-right px-3 py-2">Aplicado</th>
                <th className="text-right px-3 py-2">Valor da cota</th>
                <th className="text-right px-3 py-2">Cotas restantes</th>
                <th className="text-right px-3 py-2">Dias</th>
                <th className="text-left px-5 py-2">IOF</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-6 text-slate-500">Carregando…</td></tr>
              ) : applications.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-6 text-slate-500">Nenhuma aplicação registrada</td></tr>
              ) : applications.map((a) => {
                const lot = summary?.lots?.find((l: any) => l.applicationId === a.id);
                const spent = Number(a.remainingQuotas) <= 0;
                return (
                  <tr key={a.id} className={`border-t border-slate-700/60 ${spent ? 'opacity-50' : ''}`}>
                    <td className="px-5 py-2.5 text-slate-300">{fmtDate(a.date)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-300">R$ {fmt(a.amount)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-400">{fmtQ(a.quotaValue)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-400">{fmtQ(a.remainingQuotas)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-400">{lot ? lot.daysHeld : '—'}</td>
                    <td className="px-5 py-2.5">
                      {spent ? (
                        <span className="text-xs text-slate-500">consumido</span>
                      ) : lot?.isIofFree ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">livre</span>
                      ) : lot ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400">
                          livre em {fmtDate(lot.iofFreeDate)}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== Resgates ===== */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700">
          <h3 className="text-white font-semibold">Resgates</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-slate-400 text-xs">
              <tr>
                <th className="text-left px-5 py-2">Data</th>
                <th className="text-right px-3 py-2">Bruto</th>
                <th className="text-right px-3 py-2">Rendimento</th>
                <th className="text-right px-3 py-2">IOF</th>
                <th className="text-right px-3 py-2">IRRF</th>
                <th className="text-right px-3 py-2">Líquido na conta</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {redemptions.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-6 text-slate-500">Nenhum resgate registrado</td></tr>
              ) : redemptions.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-slate-700/60">
                    <td className="px-5 py-2.5 text-slate-300">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-300">R$ {fmt(r.grossAmount)}</td>
                    <td className="px-3 py-2.5 text-right text-green-400">R$ {fmt(r.grossYield)}</td>
                    <td className="px-3 py-2.5 text-right text-amber-400">R$ {fmt(r.iofAmount)}</td>
                    <td className="px-3 py-2.5 text-right text-amber-400">R$ {fmt(r.irrfAmount)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-200 font-medium">R$ {fmt(r.netAmount)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => setExpandedRedemption(expandedRedemption === r.id ? null : r.id)}
                        className="text-xs text-slate-300 hover:text-white"
                      >
                        {expandedRedemption === r.id ? 'ocultar' : 'ver lotes'}
                      </button>
                    </td>
                  </tr>
                  {expandedRedemption === r.id && (r.lots || []).map((l: any) => (
                    <tr key={l.id} className="bg-slate-900/40 text-xs">
                      <td className="px-5 py-1.5 pl-10 text-slate-500">
                        lote de {fmtDate(applications.find((a) => a.id === l.applicationId)?.date) || '—'} · {l.daysHeld} dias
                      </td>
                      <td className="px-3 py-1.5 text-right text-slate-400">R$ {fmt(l.grossAmount)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-400">R$ {fmt(l.grossYield)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-400">
                        R$ {fmt(l.iofAmount)} <span className="text-slate-600">({Math.round(Number(l.iofRate) * 100)}%)</span>
                      </td>
                      <td className="px-3 py-1.5 text-right text-slate-400">
                        R$ {fmt(l.irrfAmount)} <span className="text-slate-600">({Math.round(Number(l.irrfRate) * 1000) / 10}%)</span>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== Posições informadas pelo banco ===== */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-white font-semibold">Posições do relatório</h3>
          <span className="text-xs text-slate-500">Serve de conferência contra o que o HUB calcula</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-slate-400 text-xs">
              <tr>
                <th className="text-left px-5 py-2">Data</th>
                <th className="text-right px-3 py-2">Saldo bruto</th>
                <th className="text-right px-3 py-2">Provisão IOF</th>
                <th className="text-right px-3 py-2">Provisão IRRF</th>
                <th className="text-right px-3 py-2">Líquido para saque</th>
                <th className="text-right px-5 py-2">Valor da cota</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-6 text-slate-500">Nenhuma posição registrada</td></tr>
              ) : positions.map((p) => (
                <tr key={p.id} className="border-t border-slate-700/60">
                  <td className="px-5 py-2.5 text-slate-300">{fmtDate(p.date)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-300">R$ {fmt(p.grossBalance)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-400">R$ {fmt(p.iofProvision)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-400">R$ {fmt(p.irrfProvision)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-200">R$ {fmt(p.netWithdrawable)}</td>
                  <td className="px-5 py-2.5 text-right text-slate-400">{fmtQ(p.quotaValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      </div>
      </EstadoDaLista>
      )}

      {importing && (
        <StatementImportModal
          account={account}
          onClose={() => setImporting(false)}
          onImported={async () => { await load(); onChanged(); }}
        />
      )}

      {modal && (
        <MovementModal
          account={account}
          initialTab={modal}
          summary={summary}
          onClose={() => setModal(null)}
          onSaved={afterWrite}
        />
      )}
    </>
  );
}

const ROTULO_DA_VARREDURA: Record<string, string> = { aplicacao: 'Aplicação', resgate: 'Resgate', rendimento: 'Rendimento' };

/**
 * As movimentações do fundo de varredura, lidas da conta corrente pela prévia do fundSweep (só leitura). A linha que o
 * fundSweep deixa de fora (o tipo do extrato contradiz o rótulo) aparece com o motivo, porque some do saldo.
 */
function MovimentacoesDaVarredura({ temCorrente, carga, linhas, onTentarDeNovo }: {
  temCorrente: boolean; carga: ReturnType<typeof useEstadoDaCarga>; linhas: any[]; onTentarDeNovo: () => void;
}) {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden" data-movimentacoes-da-varredura>
      <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between">
        <h3 className="text-white font-semibold">Movimentações na conta corrente</h3>
        <span className="text-xs text-slate-500">As aplicações e resgates automáticos que formam o saldo acima</span>
      </div>
      {!temCorrente ? (
        <p className="px-5 py-6 text-sm text-slate-500">Esta aplicação não está ligada a uma conta corrente: não há de onde ler as movimentações.</p>
      ) : (
      <EstadoDaLista carga={carga} temDados={carga.atualizadoEm !== null} oQue="as movimentações da conta corrente" onTentarDeNovo={onTentarDeNovo} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-slate-400 text-xs">
              <tr>
                <th className="text-left px-5 py-2">Data</th>
                <th className="text-left px-3 py-2">Movimento</th>
                <th className="text-right px-5 py-2">Valor</th>
              </tr>
            </thead>
            <tbody>
              {linhas.length === 0 ? (
                <tr><td colSpan={3} className="text-center py-6 text-slate-500">Nenhuma aplicação ou resgate na conta corrente</td></tr>
              ) : linhas.map((l) => (
                <tr key={l.id} className="border-t border-slate-700/60" data-movimento={l.rotulo}>
                  <td className={`px-5 py-2.5 ${l.rotulo === 'rendimento' ? 'text-slate-500' : 'text-slate-300'}`}>{fmtDate(l.date)}</td>
                  <td className={`px-3 py-2.5 ${l.rotulo === 'rendimento' ? 'text-slate-500' : 'text-slate-300'}`}>
                    {ROTULO_DA_VARREDURA[l.rotulo] ?? l.rotulo}
                    {l.ignorada && <span className={`block text-xs ${TONE_TEXT.warning}`}>fora do saldo{l.motivo ? `: ${l.motivo}` : ''}</span>}
                    {/* Rendimento: está no extrato, mas o fundSweep não o soma (efeito zero no fundo). Cinza, não escondido:
                        sem a linha a lista pareceria incompleta contra o extrato. */}
                    {!l.ignorada && l.rotulo === 'rendimento' && <span className="block text-xs text-slate-500">não entra no saldo</span>}
                  </td>
                  <td className={`px-5 py-2.5 text-right ${l.ignorada ? 'text-slate-500 line-through' : l.rotulo === 'rendimento' ? 'text-slate-500' : 'text-slate-200'}`}>R$ {fmt(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </EstadoDaLista>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modal de registro — espelha o "Extrato de aplicação" do Sicredi.     */
/* ------------------------------------------------------------------ */

/**
 * Campo de formulário. Vive no escopo do MÓDULO, não dentro do modal: um
 * componente declarado dentro de outro é recriado a cada render, o React
 * remonta o <input> e o foco se perde a cada tecla digitada.
 */
function Field({ label, k, hint, type = 'text', value, onChange }: {
  label: string; k: string; hint?: string; type?: string;
  value: any; onChange: (k: string, v: any) => void;
}) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(k, e.target.value)}
        className={`w-full rounded-lg px-3 py-2 text-sm outline-none ${campo}`}
      />
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function MovementModal({ account, initialTab, summary, onClose, onSaved }: {
  account: any; initialTab: Tab; summary: any; onClose: () => void; onSaved: () => void;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Transações candidatas da conta corrente de origem, para vincular a ponta
  // bancária. Sem o vínculo a linha do extrato fica órfã e volta a aparecer
  // como pendência de conciliação.
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candidatasErro, setCandidatasErro] = useState<string | null>(null);
  useEffect(() => {
    if (!account.parentAccountId) return;
    (async () => {
      try {
        const res = await bankTransactionsService.getAll({
          bankAccountId: account.parentAccountId,
          limit: 300,
        });
        const list = Array.isArray(res) ? res : res?.data || [];
        setCandidates(
          list.filter((t: any) => /aplic|resg|fundo|captacao|captação/i.test(t.description || '')),
        );
        setCandidatasErro(null);
      } catch (e) {
        // O vínculo é opcional, mas o select vazio calado fazia gravar sem a ponta bancária sem saber por quê.
        setCandidatasErro(mensagemDoErroDeCarga(e));
      }
    })();
  }, [account.parentAccountId]);

  const [form, setForm] = useState<any>({
    date: todayStr(),
    amount: '',
    quotaValue: '',
    quotas: '',
    grossAmount: '',
    actualIofAmount: '',
    actualIrrfAmount: '',
    actualNetAmount: '',
    grossBalance: '',
    iofProvision: '',
    irrfProvision: '',
    netWithdrawable: '',
    totalQuotas: '',
    bankTransactionId: '',
    notes: '',
  });
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const num = (v: any) => {
    if (v === '' || v == null) return undefined;
    const n = Number(String(v).replace(',', '.'));
    return isFinite(n) ? n : undefined;
  };

  // Cotas derivadas de valor ÷ valor da cota. Editável porque o relatório traz o
  // número exato do banco, e é ele que deve prevalecer sobre a divisão.
  useEffect(() => {
    const a = num(form.amount), q = num(form.quotaValue);
    if (tab === 'aplicacao' && a && q) {
      set('quotas', String(Math.round((a / q) * 1e8) / 1e8));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount, form.quotaValue, tab]);

  const [plan, setPlan] = useState<any>(null);
  const [planning, setPlanning] = useState(false);

  const simulate = async () => {
    setError(null); setPlanning(true); setPlan(null);
    try {
      setPlan(await investmentsService.planRedemption({
        bankAccountId: account.id,
        date: form.date,
        quotaValue: num(form.quotaValue),
        grossAmount: num(form.grossAmount),
      }));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Falha ao simular');
    } finally { setPlanning(false); }
  };

  const save = async () => {
    setError(null); setSaving(true);
    try {
      if (tab === 'aplicacao') {
        await investmentsService.createApplication({
          bankAccountId: account.id,
          date: form.date,
          amount: num(form.amount),
          quotas: num(form.quotas),
          quotaValue: num(form.quotaValue),
          bankTransactionId: num(form.bankTransactionId),
          notes: form.notes || undefined,
        });
      } else if (tab === 'resgate') {
        await investmentsService.createRedemption({
          bankAccountId: account.id,
          date: form.date,
          quotaValue: num(form.quotaValue),
          grossAmount: num(form.grossAmount),
          actualIofAmount: num(form.actualIofAmount),
          actualIrrfAmount: num(form.actualIrrfAmount),
          actualNetAmount: num(form.actualNetAmount),
          bankTransactionId: num(form.bankTransactionId),
          notes: form.notes || undefined,
        });
      } else {
        await investmentsService.savePosition({
          bankAccountId: account.id,
          date: form.date,
          grossBalance: num(form.grossBalance),
          iofProvision: num(form.iofProvision),
          irrfProvision: num(form.irrfProvision),
          netWithdrawable: num(form.netWithdrawable),
          totalQuotas: num(form.totalQuotas),
          quotaValue: num(form.quotaValue),
          notes: form.notes || undefined,
        });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Falha ao gravar');
    } finally { setSaving(false); }
  };

  // Field é declarado FORA deste componente de propósito. Definido aqui dentro,
  // ele seria uma função nova a cada render, o React trocaria o <input> por um
  // elemento diferente a cada tecla e o campo perderia o foco após cada
  // caractere — bug clássico de formulário aninhado.
  const field = (label: string, k: string, hint?: string, type = 'text') => (
    <Field label={label} k={k} hint={hint} type={type} value={form[k]} onChange={set} />
  );

  const txSelect = (
    <div>
      <label className="block text-xs text-slate-400 mb-1">Linha do extrato (opcional)</label>
      <select
        value={form.bankTransactionId}
        onChange={(e) => set('bankTransactionId', e.target.value)}
        className={`w-full rounded-lg px-3 py-2 text-sm outline-none ${campo}`}
      >
        <option value="">Não vincular</option>
        {candidates.map((t) => (
          <option key={t.id} value={t.id}>
            {fmtDate(t.date)} · {t.type === 'credit' ? '+' : '−'}R$ {fmt(t.amount)} · {t.description}
          </option>
        ))}
      </select>
      {candidatasErro && (
        <p className={`text-[11px] mt-1 ${TONE_TEXT.warning}`} data-testid="candidatas-erro">
          Não consegui ler as linhas da conta de origem: {candidatasErro}. Sem o vínculo, a linha do extrato volta como pendência.
        </p>
      )}
      <p className="text-[11px] text-slate-500 mt-1">
        Vincular marca a linha como Movimentação Financeira e tira ela das pendências.
      </p>
    </div>
  );

  return (
    // Formulário digitado: antes fechava ao clicar fora; agora só pelo × ou
    // Cancelar. As abas são as da casca; trocar de aba limpa prévia e erro, como antes.
    <CascaDeModal
      largura="base"
      titulo="Registrar movimento"
      subtitulo={account.investmentProduct}
      onClose={onClose}
      abas={[
        { chave: 'aplicacao', rotulo: 'Aplicação' },
        { chave: 'resgate', rotulo: 'Resgate' },
        { chave: 'posicao', rotulo: 'Posição' },
      ]}
      abaAtiva={tab}
      onAba={(k) => { setTab(k as Tab); setPlan(null); setError(null); }}
      corpoClassName="p-5 space-y-4"
      rodape={
        <div className="ml-auto flex gap-2">
          <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:text-white text-sm">Cancelar</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium disabled:opacity-40"
          >
            {saving ? 'Gravando…' : 'Gravar'}
          </button>
        </div>
      }
    >
          <div className="bg-central-fundo/60 rounded-lg p-3 text-xs text-slate-400">
            Copie do <strong className="text-slate-300">Extrato de aplicação</strong> do Sicredi.
            {tab === 'aplicacao' && ' Use a data da linha APLICACAO na tabela de Movimentação, não a "Data Aplicação" do cabeçalho.'}
            {tab === 'posicao' && ' Os valores estão no bloco "Posição para Saque".'}
          </div>

          {tab === 'aplicacao' && (
            <div className="grid grid-cols-2 gap-4">
              {field('Data da aplicação', 'date', undefined, 'date')}
              {field('Valor aplicado (R$)', 'amount')}
              {field('Valor da cota', 'quotaValue', '8 casas decimais')}
              {field('Cotas', 'quotas', 'calculado; ajuste para o número exato do relatório')}
              <div className="col-span-2">{txSelect}</div>
            </div>
          )}

          {tab === 'resgate' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                {field('Data do resgate', 'date', undefined, 'date')}
                {field('Valor da cota no resgate', 'quotaValue')}
                {field('Valor bruto resgatado (R$)', 'grossAmount')}
                <div className="flex items-end">
                  <button
                    onClick={simulate}
                    disabled={planning || !form.quotaValue || !form.grossAmount}
                    className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm disabled:opacity-40"
                  >
                    {planning ? 'Simulando…' : 'Simular custo'}
                  </button>
                </div>
              </div>

              {plan && (
                <div className="bg-central-fundo/60 border border-central-borda rounded-lg p-4 space-y-2">
                  <p className="text-sm text-white font-medium">Simulação</p>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div><p className="text-xs text-slate-500">Rendimento</p><p className="text-green-400">R$ {fmt(plan.grossYield)}</p></div>
                    <div><p className="text-xs text-slate-500">IOF</p><p className="text-amber-400">R$ {fmt(plan.iofAmount)}</p></div>
                    <div><p className="text-xs text-slate-500">IRRF</p><p className="text-amber-400">R$ {fmt(plan.irrfAmount)}</p></div>
                    <div><p className="text-xs text-slate-500">Cai na conta</p><p className="text-white font-semibold">R$ {fmt(plan.netAmount)}</p></div>
                  </div>
                  <div className="pt-2 border-t border-central-borda/60 space-y-1">
                    {plan.lots.map((l: any, i: number) => (
                      <p key={i} className="text-xs text-slate-400">
                        lote de {fmtDate(l.applicationDate)} ({l.daysHeld} dias): R$ {fmt(l.grossAmount)} ·
                        IOF {Math.round(l.iofRate * 100)}% = R$ {fmt(l.iofAmount)}
                      </p>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 pt-1">
                    Custo total do resgate: <strong className="text-amber-400">R$ {fmt(plan.iofAmount + plan.irrfAmount)}</strong>
                  </p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                {field('IOF cobrado (R$)', 'actualIofAmount', 'opcional; vence o calculado')}
                {field('IRRF cobrado (R$)', 'actualIrrfAmount', 'opcional')}
                {field('Líquido creditado (R$)', 'actualNetAmount', 'o valor que caiu no extrato')}
              </div>
              {txSelect}
            </>
          )}

          {tab === 'posicao' && (
            <div className="grid grid-cols-2 gap-4">
              {field('Data da posição', 'date', undefined, 'date')}
              {field('Saldo bruto (R$)', 'grossBalance')}
              {field('Provisão IOF (R$)', 'iofProvision')}
              {field('Provisão IRRF (R$)', 'irrfProvision')}
              {field('Líquido para saque (R$)', 'netWithdrawable', 'deixe vazio para calcular')}
              {field('Total de cotas', 'totalQuotas')}
              {field('Valor da cota', 'quotaValue')}
            </div>
          )}

          {field('Observação (opcional)', 'notes')}

          {error && (
            <div className={`border rounded-lg p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>{error}</div>
          )}
    </CascaDeModal>
  );
}

/* ------------------------------------------------------------------ */
/* Import do "Extrato de aplicação" (PDF do Sicredi)                    */
/* ------------------------------------------------------------------ */

// Situação da prévia: tons do mapa (§4). `merged` era céu, e céu agora é `future`:
// vira `info` (informa que a linha entra somada a outra).
const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  new: { label: 'novo', cls: `${TONE_BADGE.success} ${TONE_BORDER.success}` },
  duplicate: { label: 'já existe', cls: `${TONE_BADGE.neutral} ${TONE_BORDER.neutral}` },
  update: { label: 'atualiza', cls: `${TONE_BADGE.warning} ${TONE_BORDER.warning}` },
  unsupported: { label: 'não importa', cls: `${TONE_BADGE.danger} ${TONE_BORDER.danger}` },
  // Não vira registro próprio: é absorvido por outra linha. Hoje só o encargo de
  // IOF/IRRF, que o fundo cobra resgatando cotas e por isso entra somado ao
  // resgate do mesmo dia.
  merged: { label: 'vai no resgate', cls: `${TONE_BADGE.info} ${TONE_BORDER.info}` },
};

const KIND_LABEL: Record<string, string> = {
  application: 'Aplicação',
  redemption: 'Resgate',
  position: 'Posição',
  charge: 'Encargo',
  come_cotas: 'Come-cotas',
  unknown: 'Desconhecido',
};

/**
 * Fluxo de duas etapas de propósito: o relatório é sempre consultado por
 * período, e o período seguinte repete as linhas do anterior. Sem ver a prévia
 * antes de gravar, reimportar duplicaria lote — e lote duplicado quebra o FIFO,
 * que é o que faz a conta de IOF do módulo inteiro.
 */
function StatementImportModal({ account, onClose, onImported }: {
  account: any; onClose: () => void; onImported: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readable = (e: any) =>
    e?.response?.data?.message || e?.message || 'Falha ao processar o arquivo';

  // D6 (10/10/2026): o dropzone não travava enquanto lia. Prévia do arquivo A pendente → soltar B → a prévia de A
  // chegava e ficava na tela, e "Importar" mandava B: gravava o que ninguém viu. Agora a troca espera a leitura, e
  // uma prévia só entra se for do arquivo que está na tela.
  const arquivoDaTela = useRef<File | null>(null);
  const pick = async (f: File | null | undefined) => {
    if (!f || busy) return;
    arquivoDaTela.current = f;
    setFile(f); setPreview(null); setResult(null); setError(null); setBusy(true);
    try {
      const prev = await investmentsService.previewStatement(account.id, f);
      if (arquivoDaTela.current === f) setPreview(prev);
    } catch (e: any) {
      if (arquivoDaTela.current === f) setError(readable(e));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const r = await investmentsService.importStatement(account.id, file);
      setResult(r);
      setPreview(r);
      onImported();
    } catch (e: any) {
      setError(readable(e));
    } finally {
      setBusy(false);
    }
  };

  const pending = preview?.counts ? preview.counts.new + preview.counts.update : 0;

  return (
    // Arquivo e prévia na tela: antes fechava ao clicar fora; agora só pelo × ou
    // Cancelar. Fechar sem querer obrigaria a reler o PDF.
    <CascaDeModal
      largura="md"
      titulo="Importar extrato de aplicação"
      subtitulo={account.investmentProduct}
      onClose={onClose}
      corpoClassName="p-5 space-y-4"
      rodape={
        <div className="ml-auto flex gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white text-sm">
            {result ? 'Fechar' : 'Cancelar'}
          </button>
          {!result && (
            <button
              onClick={doImport}
              disabled={busy || !preview || pending === 0}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? 'Importando…' : pending > 0 ? `Importar ${pending} lançamento(s)` : 'Nada a importar'}
            </button>
          )}
        </div>
      }
    >
          <div className="bg-central-fundo/60 rounded-lg p-3 text-xs text-slate-400">
            No Internet Banking Sicredi: <strong className="text-slate-300">Investimentos → Posição consolidada → Extrato</strong>,
            escolha o período e clique em <strong className="text-slate-300">gerar PDF</strong>.
            Um arquivo traz aplicações, resgates e a posição do dia.
          </div>

          {/* Dropzone */}
          <label
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
            aria-disabled={busy}
            data-testid="dropzone-do-extrato"
            className={`block border-2 border-dashed rounded-lg p-6 text-center transition-colors ${busy ? 'opacity-60 cursor-wait' : 'cursor-pointer'} ${
              dragging ? 'border-primary bg-central-elevado' : 'border-slate-600 hover:border-slate-500'
            }`}
          >
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              disabled={busy}
              onChange={(e) => pick(e.target.files?.[0])}
            />
            <p className="text-sm text-slate-300">{busy && !result ? 'Lendo o arquivo… espere a prévia antes de trocar' : 'Arraste o PDF aqui ou clique para escolher'}</p>
            {file && <p className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
          </label>

          {busy && <p className="text-sm text-slate-400">Lendo o relatório…</p>}

          {error && (
            <div className={`border rounded-lg p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>
              {error}
            </div>
          )}

          {preview && (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-400">
                <p>Produto: <span className="text-slate-300">{preview.file.product || '—'}</span></p>
                <p>Período: <span className="text-slate-300">{preview.file.period || '—'}</span></p>
                <p>CNPJ do fundo: <span className="text-slate-300">{preview.file.fundCnpj || '—'}</span></p>
                <p>Posição em: <span className="text-slate-300">{preview.file.positionDate ? fmtDate(preview.file.positionDate) : '—'}</span></p>
              </div>

              <div className="border border-central-borda rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-central-fundo/60 text-slate-400 text-xs">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Data</th>
                      <th className="px-4 py-2 text-left font-medium">Lançamento</th>
                      <th className="px-4 py-2 text-right font-medium">Valor</th>
                      <th className="px-4 py-2 text-right font-medium">Cotas</th>
                      <th className="px-4 py-2 text-left font-medium">Situação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/60">
                    {preview.entries.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-4 text-center text-slate-500 text-xs">
                        Nenhuma movimentação no período do relatório.
                      </td></tr>
                    )}
                    {preview.entries.map((e: any, i: number) => {
                      const st = STATUS_STYLE[e.status] ?? STATUS_STYLE.duplicate;
                      return (
                        <tr key={i} className={e.status === 'duplicate' ? 'opacity-60' : ''}>
                          <td className="px-4 py-2 text-slate-300 whitespace-nowrap">{fmtDate(e.date)}</td>
                          <td className="px-4 py-2 text-slate-300">
                            {KIND_LABEL[e.kind] ?? e.kind}
                            <span className="text-slate-500 text-xs ml-2">{e.description}</span>
                            {e.reason && <p className="text-[11px] text-slate-500 mt-0.5">{e.reason}</p>}
                          </td>
                          <td className="px-4 py-2 text-right text-slate-300 whitespace-nowrap">R$ {fmt(e.amount)}</td>
                          <td className="px-4 py-2 text-right text-slate-400 whitespace-nowrap">{e.quotas != null ? fmtQ(e.quotas) : '—'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded border text-xs whitespace-nowrap ${st.cls}`}>{st.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {preview.warnings?.map((w: string, i: number) => (
                <div key={i} className={`border rounded-lg p-3 text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>
                  {w}
                </div>
              ))}

              {result && (
                <div className={`border rounded-lg p-3 text-sm ${TONE_SURFACE.success} ${TONE_BORDER.success} ${TONE_TEXT.success}`}>
                  Importado: {result.imported.applications} aplicação(ões), {result.imported.redemptions} resgate(s), {result.imported.positions} posição(ões).
                </div>
              )}
            </>
          )}
    </CascaDeModal>
  );
}
